import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { MyScenario } from '../../services/scenario';
import { resolveScenarioSkills, type ScenarioSkillEntry } from '../../services/scenarioSkills';
import { parseScenarioTriggers, parseScenarioWorkflow } from '../../services/scenarioPresentation';
import { i18nService } from '../../services/i18n';
import ScenarioAppIcon from './ScenarioAppIcon';

/** 场景详情只读预览；确认后委托现有准备/导航入口，不在展示组件安装或启动任务。 */
export default function ScenarioDetailDialog({
  scenario,
  onClose,
  onUse,
  useLabel,
}: {
  scenario: MyScenario;
  onClose: () => void;
  onUse: (scenario: MyScenario) => void | boolean | Promise<void | boolean>;
  /** 入口区分新建任务和应用到现有对话，展示组件不决定目标。 */
  useLabel?: string;
}) {
  const reducedMotion = useReducedMotion();
  const [entries, setEntries] = useState<ScenarioSkillEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const alive = useRef(true);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    alive.current = true;
    const invalidate = () => {
      alive.current = false;
      close.current();
    };
    window.addEventListener('auth:identityChanging', invalidate);
    return () => {
      alive.current = false;
      window.removeEventListener('auth:identityChanging', invalidate);
    };
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    setEntries([]);
    void resolveScenarioSkills(scenario, { force: true })
      .then((result) => {
        if (current && alive.current) setEntries(result.entries);
      })
      .catch(() => {
        if (current && alive.current) setError(true);
      })
      .finally(() => {
        if (current && alive.current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [scenario, revision]);
  /** 同步锁防止连续确认；失败留在详情重试，晚返回不关闭其他场景的弹窗。 */
  const handleUseScenario = async () => {
    if (pending.current || loading || error) return;
    pending.current = true;
    setBusy(true);
    try {
      const result = await onUse(scenario);
      if (!alive.current) return;
      if (result === false) setError(true);
      else onClose();
    } catch {
      if (alive.current) setError(true);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const steps = parseScenarioWorkflow(scenario.workflow_steps);
  const triggers = parseScenarioTriggers(scenario.trigger_prompts);
  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      className="fixed inset-0 z-[190] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]"
    >
      <Dialog.Panel
        as={motion.div}
        initial={reducedMotion ? false : { opacity: 0, y: 10, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: reducedMotion ? 0 : 0.2 }}
        className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-claude-border bg-white shadow-2xl dark:border-claude-darkBorder dark:bg-claude-darkSurface"
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-claude-border p-5 dark:border-claude-darkBorder">
          <ScenarioAppIcon scenario={scenario} size={40} />
          <div className="min-w-0 flex-1">
            <Dialog.Title className="break-words text-lg font-semibold">
              {scenario.name}
            </Dialog.Title>
            <p className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {scenario.group_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={i18nService.t('close')}
            className="rounded-lg p-2 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>
        <div className="min-h-0 space-y-5 overflow-y-auto p-5 text-sm">
          <Dialog.Description className="whitespace-pre-wrap break-words leading-6">
            {scenario.description}
          </Dialog.Description>
          {(scenario.expert_name || scenario.greeting) && (
            <section className="rounded-xl bg-claude-accent/5 p-4">
              <h3 className="font-medium">{scenario.expert_name}</h3>
              <p className="mt-1 whitespace-pre-wrap break-words leading-6 text-claude-textSecondary dark:text-claude-darkTextSecondary">
                {scenario.greeting}
              </p>
            </section>
          )}
          {steps.length > 0 && (
            <section>
              <h3 className="mb-2 font-medium">{i18nService.t('scenariosWorkflow')}</h3>
              <ol className="space-y-2">
                {steps.map((step, index) => (
                  <li
                    key={index}
                    className="flex gap-3 rounded-xl border border-claude-border p-3 dark:border-claude-darkBorder"
                  >
                    <span className="text-claude-accent">{index + 1}</span>
                    <div className="min-w-0 break-words">
                      <p className="font-medium">{step.title}</p>
                      <p className="mt-1 whitespace-pre-wrap text-xs leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
                        {step.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}
          {triggers.length > 0 && (
            <section>
              <h3 className="mb-2 font-medium">{i18nService.t('skillsFeaturedDetailTryAsking')}</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {triggers.slice(0, 4).map((prompt, index) => (
                  <div key={index} className="rounded-xl bg-black/[.025] p-3 dark:bg-white/[.04]">
                    <p className="font-medium">{prompt.label}</p>
                    <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
                      {prompt.prompt}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section aria-busy={loading}>
            <h3 className="mb-2 font-medium">{i18nService.t('scenariosLinkedSkills')}</h3>
            {loading && <p role="status">{i18nService.t('loading')}</p>}
            {error && (
              <div role="alert" className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <span>{i18nService.t('scenarioDetailsLoadFailed')}</span>
                <button
                  type="button"
                  onClick={() => setRevision((value) => value + 1)}
                  className="shrink-0 rounded-lg border px-2 py-1"
                >
                  {i18nService.t('retry')}
                </button>
              </div>
            )}
            {!loading &&
              !error &&
              (entries.length ? (
                <ul className="space-y-2">
                  {entries.map((entry) => (
                    <li
                      key={entry.link.skill_name}
                      className="flex items-center gap-2 rounded-lg border border-claude-border p-3 dark:border-claude-darkBorder"
                    >
                      {entry.state === 'ready' ? (
                        <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" />
                      ) : (
                        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" />
                      )}
                      <span className="min-w-0 flex-1 break-words">
                        {entry.link.display_name || entry.link.skill_name}
                      </span>
                      <span className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                        {i18nService.t(
                          `scenarioSkillState${entry.state[0].toUpperCase()}${entry.state.slice(1)}`
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                  {i18nService.t('scenarioDetailsNoSkills')}
                </p>
              ))}
          </section>
          {scenario.recommended_model && (
            <p className="break-words text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {i18nService
                .t('scenarioDetailsRecommendedModel')
                .replace('{model}', scenario.recommended_model)}
            </p>
          )}
        </div>
        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-claude-border p-4 dark:border-claude-darkBorder">
          <p className="max-w-sm text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
            {i18nService.t('scenarioDetailsReadOnlyHint')}
          </p>
          <button
            type="button"
            onClick={() => void handleUseScenario()}
            disabled={busy || loading || error}
            className="inline-flex items-center gap-2 rounded-xl bg-claude-accent px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {busy ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin motion-reduce:animate-none" />
            ) : (
              <ArrowRightIcon className="h-4 w-4" />
            )}
            {useLabel || i18nService.t('scenariosStartChat')}
          </button>
        </footer>
      </Dialog.Panel>
    </Dialog>
  );
}
